import axiosInstance from '../../../core/api/axios';
import { IExercise } from '../../../core/models/exercise';
import { useExercisePrintStore } from '../stores/exercisePrintStore';
import { useExercisePageStore } from '../stores/exercisesPageStore';
import { useExerciseStore } from '../stores/exercisesStore';
import { useNewSubjectStore } from '../stores/newSubjectStore';
import { organizeExerciseField } from '../utils/exerciseHelper';

export async function saveExerciseAnswer(
  slug: string,
  answers: string[],
  correct: boolean,
  time: number,
  answerOrigin?: string
): Promise<IExercise> {
  try {
    const response = await axiosInstance.post(`/exercises/${slug}/answer`, {
      userAnswer: answers,
      correct,
      time,
      ...(answerOrigin && { answerOrigin }),
    });

    return response.data;
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function getExercise(slug: string): Promise<IExercise> {
  try {
    const response = await axiosInstance.get(`/exercises/${slug}`);

    return response.data;
  } catch (e: any) {
    throw new Error(e);
  }
}

export const getExercisesRequest = async (module: string, operation: string, page: number) => {
  try {
    const setFilters = useExercisePageStore.getState().setExercisesFilterList;
    const setFullExerciseFilterList = useExercisePageStore.getState().setFullExerciseFilterList;
    const favorite = useExerciseStore.getState().favorite;
    const unanswered = useExerciseStore.getState().unanswered;
    const wrongAnswer = useExerciseStore.getState().wrongAnswer;
    const rightAnswer = useExerciseStore.getState().rightAnswer;
    const difficulty = useExerciseStore.getState().difficulty;
    const selectedYear = useExerciseStore.getState().selectedYear;
    const institutionType = useExerciseStore.getState().institutionType;
    const trial = useExerciseStore.getState().trialOptions;
    const questions = useExerciseStore.getState().questionOptions;
    const selectedSubjects = useNewSubjectStore.getState().selectedSubjects;
    const isEnem = useExerciseStore.getState().isEnem;
    const setPage = useExerciseStore.getState().setPage;

    const listUrl = operation == 'list' ? '/' + operation : '';

    const url = `/subareas/${module}${listUrl}/exercises`; 

    let exerciseType: string | null = null;

    if(isEnem.includes('enem') && isEnem.length === 1) {
      exerciseType = 'enem'
    }

    if(isEnem.includes('vestibulares') && isEnem.length === 1) {
      exerciseType = 'vest'
    }

    const { data } = await axiosInstance.post(url, {
      wrongAnswer: wrongAnswer || null,
      rightAnswer: rightAnswer || null,
      unanswered: unanswered || null,
      favorite: favorite || null,
      page: page ?? 1,
      difficulty: difficulty.length > 0 ? difficulty : null,
      questionOptions: questions.length > 0 ? questions : null,
      trialOptions: trial || null,
      selectedYear: selectedYear.length > 0 ? selectedYear : null,
      institutionType: institutionType.length > 0 ? institutionType : null,
      subject: selectedSubjects.length > 0 ? selectedSubjects : null,
      exerciseType: exerciseType
    });

    setPage(page);

    if(operation !== 'list') {
      setFilters({
        page,
        module,
        favorite,
        unanswered,
        wrongAnswer,
        rightAnswer,
        difficulty,
        trial,
        questions,
        selectedYear,
        selectedSubjects,
        isEnem,
        institutionType
      })
    }else {
      setFullExerciseFilterList({
        page,
        module,
        favorite,
        unanswered,
        wrongAnswer,
        rightAnswer,
        difficulty,
        trial,
        questions,
        selectedYear,
        selectedSubjects,
        isEnem,
        institutionType
      })
    }
    
    return data;
  } catch (e: any) {
    throw new Error(e);
  }
}

const processExercise = async (data: any, slug: string) => {
  try {
    const { nextExercise, previousExercise } = useExercisePageStore.getState();
    
    const currentExerciseIndex = data.findIndex(
      (exercise: IExercise) => exercise.slug === slug
    );

    if (currentExerciseIndex >= 0 && !data[currentExerciseIndex].alternatives) {
      
      let exercise = data[currentExerciseIndex];

      if(nextExercise && nextExercise.slug === data[currentExerciseIndex].slug) {
        //Caso exista o próximo exercicio carregado e normalizado
        exercise = nextExercise;
      } else if(
        previousExercise &&
        previousExercise.slug === data[currentExerciseIndex].slug && 
        previousExercise.alternatives
      ) {
        //Caso exista um exercicio anterior carregado com a normalização
        exercise = previousExercise;
      } else {
        //Caso não haja nenhum exercicio carregado ou normalizado, carrega o exercicio atual
        exercise = await getExercise(slug);
      }

      //Normaliza o corpo do exercicio
      data[currentExerciseIndex] = organizeExerciseField(exercise);
    }
    
    return data;
  } catch (e: any) {
    throw new Error(e);
  }
}

// Garante que o exercício buscado esteja presente na lista. A lista pode vir de
// cache antigo (fullExerciseList), estar filtrada (filtros persistidos/assuntos)
// ou paginada — em qualquer um desses casos o slug atual pode não estar nela e o
// caller acaba redirecionando para o primeiro item (bug: a busca abre outro
// exercício). Se o slug não estiver na lista, buscamos o exercício direto pelo
// slug e o inserimos no início, mantendo o usuário no exercício correto.
const ensureExerciseInList = async (
  data: IExercise[],
  slug: string
): Promise<IExercise[]> => {
  if (data.some((exercise) => exercise.slug === slug)) {
    return data;
  }

  try {
    const exercise = await getExercise(slug);

    if (exercise && exercise.slug) {
      return [organizeExerciseField(exercise), ...data];
    }
  } catch {
    // Não foi possível carregar o exercício buscado — mantém a lista original e
    // deixa o caller decidir o fallback (primeiro da lista).
  }

  return data;
};

export async function getExercisesList(
  module: string,
  slug: string
): Promise<IExercise[]> {
  try {
    const { setFullExerciseList, fullExerciseList } = useExercisePageStore.getState();

    let data: any = null;
    const shouldLoadFromStore = hasExercisesLoaded(module, 'Full') && fullExerciseList.length > 0;
    
    if (!shouldLoadFromStore) {
      const { exercises } = await getExercisesRequest(module, 'list', 1);

      data = exercises;
      setFullExerciseList(exercises);
    } else {
      data = fullExerciseList;
    }

    data = await ensureExerciseInList(data, slug);

    data = await processExercise(data, slug);

    return data;
  } catch (e: any) {
    throw new Error(e);
  }
}

// Cache de filtros aplicados:
// - exercisesFilterList ('List'): cache da listagem paginada (/exercises) — consumido por ExerciseList e related content.
// - fullExerciseFilterList ('Full'): cache da lista completa (/list/exercises) — consumido pela resolução e pelo modal "Lista de conteúdo".
const hasExercisesLoaded = (module: string, type: 'Full' | 'List') => {
  const { exercisesFilterList, fullExerciseFilterList } = useExercisePageStore.getState();
  const { favorite, unanswered, wrongAnswer, rightAnswer, difficulty, questionOptions, isEnem, trialOptions, selectedYear, institutionType, setPage } = useExerciseStore.getState();
  const { selectedSubjects } = useNewSubjectStore.getState();

  if(type === 'Full') {
    if(!fullExerciseFilterList) return false;

    if(fullExerciseFilterList?.module !== module) return false;

    if(fullExerciseFilterList?.selectedSubjects && selectedSubjects) {
      if(fullExerciseFilterList?.selectedSubjects.toString() !== selectedSubjects.toString()) {
        return false;
      }
    }

    return (
      fullExerciseFilterList.favorite === favorite &&
      fullExerciseFilterList.unanswered === unanswered &&
      fullExerciseFilterList.wrongAnswer === wrongAnswer &&
      fullExerciseFilterList.rightAnswer === rightAnswer &&
      JSON.stringify(fullExerciseFilterList.difficulty) === JSON.stringify(difficulty) &&
      JSON.stringify(fullExerciseFilterList.selectedYear) === JSON.stringify(selectedYear) &&
      JSON.stringify(fullExerciseFilterList.institutionType) === JSON.stringify(institutionType) &&
      JSON.stringify(fullExerciseFilterList.questions) === JSON.stringify(questionOptions) &&
      JSON.stringify(fullExerciseFilterList.isEnem) === JSON.stringify(isEnem) &&
      fullExerciseFilterList.trial === trialOptions &&
      JSON.stringify(fullExerciseFilterList.selectedSubjects) === JSON.stringify(selectedSubjects) &&
      fullExerciseFilterList.module === module
    );
  }

  if (!exercisesFilterList) return false;

  if (exercisesFilterList.module !== module) return false;

  if(exercisesFilterList?.selectedSubjects && selectedSubjects) {
    if(exercisesFilterList?.selectedSubjects.toString() !== selectedSubjects.toString()) {
      setPage(1);
      return false;
    }
  }

  return (
    exercisesFilterList.favorite === favorite &&
    exercisesFilterList.unanswered === unanswered &&
    exercisesFilterList.wrongAnswer === wrongAnswer &&
    exercisesFilterList.rightAnswer === rightAnswer &&
    JSON.stringify(exercisesFilterList.difficulty) === JSON.stringify(difficulty) &&
    JSON.stringify(exercisesFilterList.selectedYear) === JSON.stringify(selectedYear) &&
    JSON.stringify(exercisesFilterList.institutionType) === JSON.stringify(institutionType) &&
    JSON.stringify(exercisesFilterList.questions) === JSON.stringify(questionOptions) &&
    JSON.stringify(exercisesFilterList.isEnem) === JSON.stringify(isEnem) &&
    exercisesFilterList.trial === trialOptions &&
    JSON.stringify(exercisesFilterList.selectedSubjects) === JSON.stringify(selectedSubjects) &&
    exercisesFilterList.module === module
  );
}

export async function getExercises(module: string, operation: string, page: number) {
  try {
    const { setExercises, exercises, exercisesFilterList } = useExercisePageStore.getState();
    const { setTotalExercises, exercises: storedExercises } = useExerciseStore.getState();
  
    let loadedData: IExercise[] = [];
    let returnedData: IExercise[] = []
    let hasMore = false;

    const shouldLoadFromStore = hasExercisesLoaded(module, 'List') && storedExercises.length > 0;
    const shouldAppendData = hasExercisesLoaded(module, 'List') && exercisesFilterList && page > exercisesFilterList.page;

    if (shouldLoadFromStore && !shouldAppendData) {
      loadedData = storedExercises;
      returnedData = loadedData;
      hasMore = true;
      
      setExercises(loadedData);
    } else {
      const data = await getExercisesRequest(module, operation, page);


      if(data.exercises && data.exercises.length === 20) {
        hasMore = true;
      }

      loadedData = data.exercises;
      returnedData = shouldAppendData ? [...exercises, ...loadedData] : loadedData;

      setTotalExercises(data.totalExercises);
      setExercises(returnedData);
    }

    return { returnedData, hasMore };
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function getExercisesPrint(subarea: string) {
  try {
    const setLoading = useExercisePrintStore.getState().setIsLoading;
    const setExercises = useExercisePrintStore.getState().setExercises;

    const types = useExercisePrintStore.getState().questionOptions;
    const difficulty = useExercisePrintStore.getState().difficulty;
    const favorites = useExercisePrintStore.getState().favorites;
    const subject = useExerciseStore.getState().active;
    const unanswered = useExercisePrintStore.getState().unanswered;

    setLoading(true);

    const subjects = JSON.stringify([subject?.slug]);
    const difficulties = JSON.stringify(difficulty);
    const questionsArray = JSON.stringify(types);

    let url = `/exercises/new/print?page=1`;

    url = url + `&difficulty=${difficulties}`;
    url = url + `&subject=${subjects}`;
    url = url + `&subarea=${subarea}`;
    url = url + `&questionOptions=${questionsArray}`;

    if (favorites) {
      url = url + `&favorites=${favorites}`;
    }

    if (unanswered) {
      url = url + `&unanswered=${unanswered}`;
    }

    const { data } = await axiosInstance.get(url);

    setExercises(data);
    setLoading(false);

    return data;
  } catch (e: any) {
    throw new Error(e);
  }
}
